// react
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from 'router';

// components
import { CarePage } from '@karehero/llama/pwa/pages/CarePage/CarePage';

// hooks
import { useCareProfile } from 'hooks/useCareProfile';

// posthog
import { useFeatureFlags } from 'hooks/useFeatureFlags';

// context
import { useTenant, Feature } from 'context';

/**
 * CareProfile is the care profile page.
 */
export const CareProfile = () => {
  // hooks
  const { progressByID } = useCareProfile();
  const { flags } = useFeatureFlags();
  const navigate = useNavigate();
  const { tenant } = useTenant();

  const promoted = useMemo(() => {
    const items: {
      id: string;
      title: string;
      progress: number | undefined;
      onClick: () => void | Promise<void>;
    }[] = [];

    if (flags.organiseDocuments) {
      items.push({
        id: 'key-documents',
        title: 'Key documents',
        progress: undefined,
        onClick: () => navigate(ROUTES.KEY_DOCUMENTS),
      });
      items.push({
        id: 'call-notes',
        title: 'Call notes',
        progress: undefined,
        onClick: () => navigate(ROUTES.CALLS),
      });
    }

    if (tenant.activeFeatureVersions.benefitsCalculator) {
      const route =
        tenant.activeFeatureVersions.benefitsCalculator === 'v1'
          ? ROUTES.ELIGIBILITY_CALCULATOR
          : ROUTES.ELIGIBILITY_CALCULATOR_V2;
      items.push({
        id: 'eligibility-calculator',
        title: 'Benefits eligibility calculator',
        progress: undefined,
        onClick: () => navigate(route),
      });
    }

    return items;
  }, [flags, navigate, tenant]);

  const categories = useMemo(() => {
    const items: {
      id: string;
      title: string;
      progress: number | undefined;
      onClick: () => void;
    }[] = [];

    if (tenant.features.includes(Feature.CARE_ASSESSMENT)) {
      items.push({
        id: 'care-assessment',
        title: 'Care assessment',
        progress: progressByID['care-assessment'] || 0,
        onClick: () => navigate(ROUTES.CARE_ASSESSMENT),
      });
    }

    if (flags.financialStatus) {
      items.push({
        id: 'financial-status',
        title: 'Financial status',
        progress: progressByID['financial-status'] || 0,
        onClick: () => navigate(ROUTES.FINANCIAL_STATUS),
      });
    }

    return items;
  }, [flags, navigate, progressByID, tenant]);

  const fullWidthCategories = useMemo(() => {
    const items: {
      id: string;
      title: string;
      progress: number | undefined;
      onClick: () => void;
    }[] = [];

    if (flags.attendanceAllowance) {
      items.push({
        id: 'attendance-allowance',
        title: 'Attendance allowance',
        progress: progressByID['attendance-allowance'] || 0,
        onClick: () => navigate(ROUTES.ATTENDANCE_ALLOWANCE),
      });
    }

    return items;
  }, [flags, navigate, progressByID]);

  return (
    <CarePage
      onManage={() => navigate(ROUTES.CARE_CIRCLE)}
      promoted={promoted}
      categories={categories}
      fullWidthCategories={fullWidthCategories}
    />
  );
};

export default CareProfile;
